import { FormatService } from 'tms-lib';

import { LineMilestone } from '../models/sales/line-milestone';

// The Milestones panel of the sales order line audit (Sales Order What's Happened, requirement 15;
// Jean's sheet, 23 Oct). Mirrors TMSWeb.Common.Constants.LineMilestones: the API sends CODES (D-9)
// and the words are here, in $localize. The two lists must stay in step.
export enum LineMilestones {
  confirmed = 'Confirmed',
  takenOffHold = 'TakenOffHold',
  rawMaterialStatus = 'RawMaterialStatus',
  itemStockStatus = 'ItemStockStatus',
  makingPurchaseOrder = 'MakingPurchaseOrder',
  materialPickingList = 'MaterialPickingList',
  rawMaterialBookedIn = 'RawMaterialBookedIn',
  rawMaterialCut = 'RawMaterialCut',
  makingPurchaseOrderReceived = 'MakingPurchaseOrderReceived',
  stockNumber = 'StockNumber',
  pickingList = 'PickingList',
  bookedIn = 'BookedIn',
  picked = 'Picked',
  loadedOnPallet = 'LoadedOnPallet',
  // param: warehouse
  receivedInto = 'ReceivedInto',
  despatch = 'Despatch',
}

// The state of one milestone (D-21). Mirrors TMSWeb.Common.Constants.LineMilestoneValues. Reached
// means the milestone's date / number carry it; every other value is a fixed phrase.
export enum LineMilestoneValues {
  reached = 'Reached',
  notYet = 'NotYet',
  notHeld = 'NotHeld',
  onHold = 'OnHold',
  inStockWhenOrdered = 'InStockWhenOrdered',
  // The date carries the issue transaction's date.
  issuedWhole = 'IssuedWhole',
  noneUsed = 'NoneUsed',
  confirmedDateNotRecorded = 'ConfirmedDateNotRecorded',
}

/** The milestone as Jean's sheet names it. */
export function lineMilestoneName(milestone: LineMilestone | undefined | null): string {
  switch (milestone?.code) {
    case LineMilestones.confirmed:
      return $localize`Confirmed`;
    case LineMilestones.takenOffHold:
      return $localize`Taken off hold`;
    case LineMilestones.rawMaterialStatus:
      return $localize`Raw-material status`;
    case LineMilestones.itemStockStatus:
      return $localize`Item stock status`;
    case LineMilestones.makingPurchaseOrder:
      return $localize`Making PO`;
    case LineMilestones.materialPickingList:
      return $localize`Material picking list`;
    case LineMilestones.rawMaterialBookedIn:
      return $localize`Raw material booked in`;
    case LineMilestones.rawMaterialCut:
      return $localize`Raw material cut`;
    case LineMilestones.makingPurchaseOrderReceived:
      return $localize`Making PO received`;
    case LineMilestones.stockNumber:
      return $localize`Stock number`;
    case LineMilestones.pickingList:
      return $localize`Picking list`;
    case LineMilestones.bookedIn:
      return $localize`Booked in`;
    case LineMilestones.picked:
      return $localize`Picked`;
    case LineMilestones.loadedOnPallet:
      return $localize`Loaded on pallet`;
    case LineMilestones.receivedInto: {
      // The receiving warehouse is only known once the stock has arrived.
      const warehouse = milestone.parameters?.['warehouse'];
      return warehouse ? $localize`Received into ${warehouse}` : $localize`Received into despatch warehouse`;
    }
    case LineMilestones.despatch:
      return $localize`Despatch`;
    default:
      // Anything unrecognised shows as sent rather than vanishing.
      return milestone?.code ?? '';
  }
}

/**
 * The milestone's value cell: the date (and time) it was reached, or the D-21 phrase. A reached
 * milestone with no date (a status-only one, such as the raw-material status) shows its status,
 * else its number, so the cell is never blank for something that has happened.
 */
export function lineMilestoneValueText(
  milestone: LineMilestone | undefined | null,
  formatService: FormatService,
): string {
  switch (milestone?.value) {
    case LineMilestoneValues.reached:
      if (milestone.date) {
        return formatService.dateTime(new Date(milestone.date)) ?? '';
      }
      return milestone.status || milestone.number || '';
    case LineMilestoneValues.notYet:
      return $localize`Not yet`;
    case LineMilestoneValues.notHeld:
      return $localize`Not held`;
    case LineMilestoneValues.onHold:
      return $localize`On hold`;
    case LineMilestoneValues.inStockWhenOrdered:
      return $localize`In stock when ordered`;
    case LineMilestoneValues.issuedWhole: {
      const date = milestone.date ? (formatService.date(new Date(milestone.date)) ?? '') : '';
      return date ? $localize`Issued whole ${date}` : $localize`Issued whole`;
    }
    case LineMilestoneValues.noneUsed:
      return $localize`None used`;
    case LineMilestoneValues.confirmedDateNotRecorded:
      return $localize`Confirmed (date not recorded)`;
    default:
      return milestone?.value ?? '';
  }
}
